import { useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Container, Navbar, Nav, Offcanvas } from 'react-bootstrap';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAccessibility } from '../../contexts/AccessibilityContext';
import { NavDropdown } from 'react-bootstrap';

export const Header = () => {
  const { language, toggleLanguage, t } = useLanguage();
  const { textSize, changeTextSize } = useAccessibility();
  const location = useLocation();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownTimeout = useRef(null);

  const navItems = [
    { path: '/', label: t('nav.home') },
    { path: '/about', label: t('nav.about') },
    { path: '/services', label: t('nav.services') },
    { path: '/training', label: t('nav.training') },
    { path: '/government-education', label: t('nav.govEdu') },
    { path: '/careers', label: t('nav.careers') },
    { path: '/contact', label: t('nav.contact') }
  ];

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <header className="gov-header">
      {/* Skip to content link for accessibility */}
      <a href="#main-content" className="skip-to-content">
        Skip to main content
      </a>

      {/* Top Bar */}
      <div className="gov-top-bar">
        <Container>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            {/* Official Website Notice */}
            <span className="d-none d-md-inline" style={{ fontSize: '0.8rem' }}>
              {t('header.officialWebsite')}
            </span>
            <span className="d-md-none" style={{ fontSize: '0.75rem' }}>
              IGT Official Website
            </span>

            {/* Accessibility Controls */}
            <div className="d-flex align-items-center gap-3">
              {/* Text Size Controls */}
              <div className="accessibility-controls d-none d-sm-flex">
                <span style={{ fontSize: '0.75rem', marginRight: '0.5rem' }}>
                  {t('header.textSize')}:
                </span>
                <button
                  className={`accessibility-btn ${textSize === 'small' ? 'active' : ''}`}
                  onClick={() => changeTextSize('small')}
                  aria-label="Decrease text size"
                >
                  A-
                </button>
                <button
                  className={`accessibility-btn ${textSize === 'normal' ? 'active' : ''}`}
                  onClick={() => changeTextSize('normal')}
                  aria-label="Normal text size"
                >
                  A
                </button>
                <button
                  className={`accessibility-btn ${textSize === 'large' ? 'active' : ''}`}
                  onClick={() => changeTextSize('large')}
                  aria-label="Increase text size"
                >
                  A+
                </button>
              </div>

              {/* Language Toggle */}
              <div className="language-toggle">
                <button
                  className={`language-btn ${language === 'en' ? 'active' : ''}`}
                  onClick={() => toggleLanguage('en')}
                  aria-label="Switch to English"
                >
                  English
                </button>
                <span style={{ opacity: 0.5 }}>|</span>
                <button
                  className={`language-btn ${language === 'hi' ? 'active' : ''}`}
                  onClick={() => toggleLanguage('hi')}
                  aria-label="हिंदी में बदलें"
                >
                  हिंदी
                </button>
              </div>
            </div>
          </div>
        </Container>
      </div>

      {/* Main Header with Logo */}
      <div style={{ background: 'hsl(var(--background))', padding: '0.75rem 0', borderBottom: '1px solid hsl(var(--border))' }}>
        <Container>
          <div className="d-flex align-items-center justify-content-between">
            {/* Logo */}
            <Link to="/" className="text-decoration-none d-flex align-items-center gap-3">
              {/* Logo Mark */}
              <div 
                style={{
                  width: '56px',
                  height: '56px',
                  background: 'linear-gradient(135deg, hsl(214 76% 26%) 0%, hsl(174 60% 40%) 100%)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: '700',
                  fontSize: '1.25rem',
                  fontFamily: 'IBM Plex Sans, sans-serif',
                  letterSpacing: '1px'
                }}
              >
                IGT
              </div>
              
              {/* Company Name */}
              <div className="d-none d-sm-block">
                <div style={{ 
                  fontWeight: '600', 
                  fontSize: '1.125rem', 
                  color: 'hsl(var(--primary))',
                  lineHeight: '1.2'
                }}>
                  {language === 'hi' ? 'इमेजिनेटर्स ग्लोबल टेक्नोलॉजी' : 'Imagenators Global Technology'}
                </div>
                <div style={{ 
                  fontSize: '0.75rem', 
                  color: 'hsl(var(--muted-foreground))',
                  marginTop: '2px'
                }}>
                  {language === 'hi' ? 'प्राइवेट लिमिटेड' : 'Private Limited'}
                </div>
              </div>
            </Link>

            {/* Mobile Menu Toggle */}
            <button
              className="d-lg-none btn p-2"
              onClick={() => setShowMobileMenu(true)}
              aria-label="Open menu"
              style={{ 
                background: 'hsl(var(--primary))', 
                color: 'white',
                borderRadius: '6px'
              }}
            >
              <Menu size={24} />
            </button>

            {/* Desktop CIN */}
            <div className="d-none d-lg-block text-end">
              <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                CIN: U62099UP2025PTC223917
              </div>
              <div style={{ fontSize: '0.75rem', color: 'hsl(var(--accent))' }}>
                www.igttech.com
              </div>
            </div>
          </div>
        </Container>
      </div>

      {/* Navigation Bar */}
   <nav className="gov-nav d-none d-lg-block">
  <Container>
    <Nav className="justify-content-center">

      {navItems.map((item) => {

        // 👉 SERVICES DROPDOWN
        if (item.path === '/services') {
          return (
<NavDropdown
  key={item.path}
  id="services-dropdown"
  show={showDropdown}
  onMouseEnter={() => {
    if (dropdownTimeout.current) {
      clearTimeout(dropdownTimeout.current);
    }
    setShowDropdown(true);
  }}
  onMouseLeave={() => {
    dropdownTimeout.current = setTimeout(() => {
      setShowDropdown(false);
    }, 200); // 🔥 delay fixes flicker
  }}
  title={
    <span
      onClick={(e) => {
        e.stopPropagation();
        window.location.href = "/services";
      }}
      style={{ cursor: "pointer" }}
    >
      {item.label}
    </span>
  }
  className={location.pathname.startsWith('/services') ? 'active' : ''}
>

  <NavDropdown.Item as={Link} to="/services/it-consulting">
    IT Consulting
  </NavDropdown.Item>

  <NavDropdown.Item as={Link} to="/services/software-development">
    Software Development
  </NavDropdown.Item>

  <NavDropdown.Item as={Link} to="/services/cloud-devops">
    Cloud & DevOps
  </NavDropdown.Item>

  <NavDropdown.Item as={Link} to="/services/ai">
    AI Solutions
  </NavDropdown.Item>

  <NavDropdown.Item as={Link} to="/services/erp">
    ERP Solutions
  </NavDropdown.Item>

  <NavDropdown.Item as={Link} to="/services/digital-marketing">
    Digital Marketing
  </NavDropdown.Item>

  <NavDropdown.Item as={Link} to="/services/gov-enterprise">
    Government & Enterprise
  </NavDropdown.Item>

  <NavDropdown.Divider />

  <NavDropdown.Item as={Link} to="/services">
    All Services Overview
  </NavDropdown.Item>

</NavDropdown>
          );
        }

        // 👉 NORMAL NAV ITEMS
        return (
          <Nav.Link
            key={item.path}
            as={Link}
            to={item.path}
            className={isActive(item.path) ? 'active' : ''}
          >
            {item.label}
          </Nav.Link>
        );
      })}

    </Nav>
  </Container>
</nav>

      {/* Mobile Navigation Offcanvas */}
      <Offcanvas 
        show={showMobileMenu} 
        onHide={() => setShowMobileMenu(false)}
        placement="end"
      >
        <Offcanvas.Header style={{ background: 'hsl(var(--primary))', color: 'white' }}>
          <Offcanvas.Title>Menu</Offcanvas.Title>
          <button
            onClick={() => setShowMobileMenu(false)}
            style={{ background: 'transparent', border: 'none', color: 'white' }}
            aria-label="Close menu"
          >
            <X size={24} />
          </button>
        </Offcanvas.Header>
        <Offcanvas.Body style={{ padding: 0 }}>
          <Nav className="flex-column">
            {navItems.map((item) => (
              <Nav.Link
                key={item.path}
                as={Link}
                to={item.path}
                onClick={() => setShowMobileMenu(false)}
                style={{
                  padding: '1rem 1.5rem',
                  borderBottom: '1px solid hsl(var(--border))',
                  color: isActive(item.path) ? 'hsl(var(--primary))' : 'hsl(var(--foreground))',
                  fontWeight: isActive(item.path) ? '600' : '400',
                  background: isActive(item.path) ? 'hsl(var(--secondary))' : 'transparent'
                }}
              >
                {item.label}
              </Nav.Link>
            ))}
          </Nav>
          
          {/* Mobile Text Size Controls */}
          <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid hsl(var(--border))' }}>
            <div style={{ fontSize: '0.875rem', marginBottom: '0.5rem', color: 'hsl(var(--muted-foreground))' }}>
              Text Size:
            </div>
            <div className="d-flex gap-2">
              <button
                className={`btn btn-sm ${textSize === 'small' ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => changeTextSize('small')}
              >
                A-
              </button>
              <button
                className={`btn btn-sm ${textSize === 'normal' ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => changeTextSize('normal')}
              >
                A
              </button>
              <button
                className={`btn btn-sm ${textSize === 'large' ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => changeTextSize('large')}
              >
                A+
              </button>
            </div>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </header>
  );
};
